import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { GetStudentActivities, GetTeacherActivities, UserActivitiesRequestState } from "../../../store/UserAction";
import * as Helper from '../../../util/Helper';
import StatusCode from "../../../util/StatusCode";
import ActiveToggle from "../../misc/ActiveToggle";
import TableHorizontalPagedToggle, { InfoObject } from "../../misc/TableHorizontalPagedToggle";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../store/store";



const UserViewActivities = () => {
    const { t } = useTranslation(['common', 'schoolAdministration']);

    const { userId } = useParams();
    const dispatch = useDispatch();
    const [show, setShow] = useState({ studentActivitites: true, teacherActivities: true })
    const studentActivities = useSelector<RootState, UserActivitiesRequestState>(state => state.users.studentActivities);
    const teacherActivities = useSelector<RootState, UserActivitiesRequestState>(state => state.users.teacherActivities);

    useEffect(() => {
        if (userId) {
            GetTeacherActivities(userId, { ...teacherActivities.filter })(dispatch);
            GetStudentActivities(userId, { ...studentActivities.filter })(dispatch);
        }
    }, [dispatch, userId]);

    const updateTeacherActivitiesPage = (value: number) => {
        if (userId) {
            GetTeacherActivities(userId, { ...teacherActivities.filter, page: value })(dispatch);
        }
    }

    const updateStudentActivitiesPage = (value: number) => {
        if (userId) {
            GetStudentActivities(userId, { ...studentActivities.filter, page: value })(dispatch);
        }
    }

    const updateOnlyActive = (value: boolean) => {
        if (userId) {
            GetStudentActivities(userId, { ...studentActivities.filter, onlyActive: value, page: 1 })(dispatch);
            GetTeacherActivities(userId, { ...teacherActivities.filter, onlyActive: value, page: 1 })(dispatch);
        }
    }

    const mapTeacherActivities = (activities: UserActivitiesRequestState): InfoObject[] => {
        if (activities.code !== StatusCode.COMPLETE) {
            return [];
        }

        return activities.data.values.map(x => ({
            id: x.id,
            data: [
                { value: <Link to={`/activity/${x.id}/information`}>{x.displayName}</Link> },
                { value: <Link to={`/schoolunit/${x.schoolUnitId}/information`}>{x.schoolUnitDisplayName}</Link> },
                { value: Helper.active(x.active) },
                { value: Helper.formatDateOnly(x.startDate) },
                { value: Helper.formatDateOnly(x.endDate) },
            ]
        }));
    }

    const mapStudentActivities = (activities: UserActivitiesRequestState): InfoObject[] => {
        if (activities.code !== StatusCode.COMPLETE) {
            return [];
        }

        return activities.data.values.map(x => ({
            id: x.id,
            data: [
                { value: <Link to={`/activity/${x.id}/information`} >{x.displayName}</Link> },
                { value: <Link to={`/schoolunit/${x.schoolUnitId}/information`}>{x.schoolUnitDisplayName}</Link> },
                { value: Helper.active(x.active) },
                x.studentGroupTitle ? { value: <Link to={`/group/${x.studentGroupId}/information`}>{x.studentGroupTitle}</Link> } : { value: 'Direktkopplad' }
            ]
        }));
    }

    const renderStudentActivities = () => {
        const studentInfo = mapStudentActivities(studentActivities);
        const count = Helper.getTotalCountText(studentActivities);
        return (
            <TableHorizontalPagedToggle
                header={[{ value: `${t('connections.student', { ns: 'schoolAdministration', count: 1 })} ${count}` }, { value: t('commonTableHeaders.schoolUnit', { ns: 'schoolAdministration' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }, { value: t('userViewActivities.student.group', {ns: 'schoolAdministration'}) }]}
                mobileHeader={[t('commonTableHeaders.activity', { ns: 'schoolAdministration' }), t('commonTableHeaders.schoolUnit', { ns: 'schoolAdministration' }), t('genericTableHeaders.active', { ns: 'common' }), t('userViewActivities.student.group', { ns: 'schoolAdministration' })]}
                info={studentInfo}
                show={show.studentActivitites}
                toggle={() => setShow({ ...show, studentActivitites: !show.studentActivitites })}
                displayName={`${t('connections.student', { ns: 'schoolAdministration', count: 2 })} ${count}`}
                noResultMsg={t('userViewActivities.student.noResult', { ns: 'schoolAdministration'}) }
                onChangePage={updateStudentActivitiesPage}
                requestState={studentActivities}
            />
        )
    }

    const renderTeacherActivities = () => {
        const teacherInfo = mapTeacherActivities(teacherActivities);
        const count = Helper.getTotalCountText(teacherActivities);
        return (
            <TableHorizontalPagedToggle
                header={[{ value: `${t('connections.teacher', { ns: 'schoolAdministration', count: 2 })} ${count}`}, { value: t('commonTableHeaders.schoolUnit', {ns: 'schoolAdministration'}) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }, { value: t('genericTableHeaders.startDate', { ns: 'common' }) }, { value: t('genericTableHeaders.endDate', {ns: 'common'}) }]}
                mobileHeader={[t('commonTableHeaders.activity', { ns: 'schoolAdministration' }), t('commonTableHeaders.schoolUnit', { ns: 'schoolAdministration' }), t('genericTableHeaders.active', { ns: 'common' }), t('genericTableHeaders.startDate', { ns: 'common' }), t('genericTableHeaders.endDate', { ns: 'common' })]}
                info={teacherInfo}
                show={show.teacherActivities}
                toggle={() => setShow({ ...show, teacherActivities: !show.teacherActivities })}
                displayName={`${t('connections.teacher', { ns: 'schoolAdministration', count: 2 })} ${count}`}
                noResultMsg={t('userViewActivities.teacher.noResult', {ns: 'schoolAdministration'})}
                onChangePage={updateTeacherActivitiesPage}
                requestState={teacherActivities}
            />
        )
    }

    return (<>
        <div className="header_member_table large">{t('object.schoolAdmin.activity', {ns:'common', count: 2 })}
            <span className="right">
                <ActiveToggle
                    onlyActive={studentActivities?.filter?.onlyActive ?? true}
                    update={updateOnlyActive}
                    disabled={studentActivities.code === StatusCode.PENDING || teacherActivities.code === StatusCode.PENDING}
                />
            </span>
        </div>
        {renderTeacherActivities()}
        {renderStudentActivities()}
    </>)

}

export default UserViewActivities;
